import React, { useState, useRef } from "react";
import { Upload, Send, Sparkles, Loader2 } from "lucide-react";
import { aiQuick } from "../api";

export default function PDF() {
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [pdfText, setPdfText] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPdfName(f.name);
    const url = URL.createObjectURL(f);
    setPdfUrl(url);
    // Extract text using pdfjs via window (best-effort). If unavailable, fallback to FileReader.text() for txt-like; for binary PDF, store empty.
    try {
      // attempt simple ArrayBuffer → text decode (works only for text-ish files); for real PDFs leave context empty
      const ab = await f.arrayBuffer();
      // crude string extraction
      const str = new TextDecoder("utf-8", { fatal: false }).decode(ab);
      // strip non-ascii
      const cleaned = str.replace(/[^\x20-\x7E\n]+/g, " ").replace(/\s+/g, " ").slice(0, 18000);
      setPdfText(cleaned);
    } catch { setPdfText(""); }
  };

  const loadUrl = (e) => {
    e.preventDefault();
    const u = e.target.url.value.trim();
    if (!u) return;
    setPdfUrl(u);
    setPdfName(u.split("/").pop() || "Remote PDF");
    setPdfText("");
  };

  const ask = async () => {
    if (!input.trim() || busy) return;
    const q = input;
    setInput("");
    setMessages(m => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const ctx = pdfText ? `Document context (best-effort extraction):\n${pdfText}\n\n` : "";
      const r = await aiQuick(
        `${ctx}Question: ${q}`,
        "You are an AI assistant analyzing a PDF document. If document context is provided, base your answer on it. Be concise."
      );
      setMessages(m => [...m, { role: "assistant", content: r.text }]);
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "AI unavailable." }]);
    }
    setBusy(false);
  };

  return (
    <div className="h-full flex" data-testid="app-pdf">
      <div className="flex-1 flex flex-col border-r border-white/10">
        <div className="p-2 border-b border-white/10 flex items-center gap-2">
          <button onClick={() => fileRef.current?.click()} className="btn-ghost text-xs flex items-center gap-1" data-testid="pdf-upload">
            <Upload size={12} /> Open PDF
          </button>
          <input type="file" accept="application/pdf,.pdf" hidden ref={fileRef} onChange={onFile} />
          <form onSubmit={loadUrl} className="flex items-center gap-1 flex-1">
            <input name="url" placeholder="…or paste a PDF URL" className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs" />
            <button className="btn-ghost text-xs">Load</button>
          </form>
          {pdfName && <span className="text-[10px] text-white/40 truncate max-w-[160px]">{pdfName}</span>}
        </div>
        <div className="flex-1 bg-black/40">
          {pdfUrl ? (
            <iframe src={pdfUrl} title="pdf" className="w-full h-full" data-testid="pdf-viewer" />
          ) : (
            <div className="h-full flex items-center justify-center text-white/40 text-sm">Open or paste a PDF URL to begin</div>
          )}
        </div>
      </div>

      <div className="w-96 flex flex-col bg-black/30">
        <div className="p-3 border-b border-white/10 flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/40">
          <Sparkles size={12} color="#ff7a2e" /> Ask the PDF
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {messages.length === 0 && <div className="text-xs text-white/40">Ask any question about this document.</div>}
          {messages.map((m, i) => (
            <div key={i} className={`text-xs ${m.role === "user" ? "text-right" : ""}`}>
              <div className={`inline-block max-w-[90%] px-3 py-2 rounded-lg ${m.role === "user" ? "bg-[#ff7a2e]" : "bg-white/8 border border-white/10"} whitespace-pre-wrap`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && <div className="text-xs text-white/40"><Loader2 size={12} className="inline animate-spin mr-1" /> thinking…</div>}
        </div>
        <div className="p-2 border-t border-white/10 flex gap-2">
          <input
            data-testid="pdf-ask-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="Ask about this PDF…"
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs"
          />
          <button data-testid="pdf-ask-send" onClick={ask} className="btn-accent text-xs px-3"><Send size={12} /></button>
        </div>
      </div>
    </div>
  );
}
